// src/components/VendorScreenPanel.jsx
// Shared "screen this vendor" panel — pick tracked-provider lists, generate
// a vendor_scan conflict report. Used from MyVendorsPage (confirmed vendor
// rows) and CompanyProfilePage (any company, confirmed or not — this is
// what makes pre-signature screening possible).
import { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function VendorScreenPanel({ companyId, companyName, providerLists, userId, fetchFn, onCancel }) {
  const navigate = useNavigate();
  const [checkedListIds, setCheckedListIds] = useState(new Set());
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState(null);

  function toggleList(id) {
    setCheckedListIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function handleGenerate() {
    if (generating) return;
    // Must open synchronously, inside the click handler — opening a tab
    // after the await below would be blocked as a popup by most browsers.
    const reportWindow = window.open("", "_blank");
    if (reportWindow) {
      reportWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>Generating report… — ProviderPayWatch</title>
            <style>
              body { background:#0a1628; color:#e2e8f0; font-family: system-ui, sans-serif; display:flex; align-items:center; justify-content:center; height:100vh; margin:0; }
              .box { text-align:center; }
              .spinner { width:32px; height:32px; border:3px solid rgba(245,158,11,0.2); border-top-color:#f59e0b; border-radius:50%; animation:spin 0.8s linear infinite; margin:0 auto 16px; }
              @keyframes spin { to { transform: rotate(360deg); } }
              p { color:#94a3b8; font-size:14px; }
            </style>
          </head>
          <body>
            <div class="box">
              <div class="spinner"></div>
              <p>Screening ${companyName.replace(/</g, "&lt;")}…<br>This can take a moment.</p>
            </div>
          </body>
        </html>
      `);
      reportWindow.document.close();
    }
    setGenerating(true);
    setGenError(null);
    try {
      const res = await fetchFn("/api/generate-conflict-report", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          reportType: "vendor_scan",
          companyId,
          trackedListIds: [...checkedListIds],
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status === "failed") {
        setGenError(data.error || "Report generation failed. Please try again.");
        if (reportWindow) reportWindow.close();
        return;
      }
      if (reportWindow) {
        reportWindow.location.href = `/conflict-report/${data.reportId}`;
      } else {
        navigate(`/conflict-report/${data.reportId}`);
      }
      onCancel();
    } catch {
      setGenError("Network error. Please try again.");
      if (reportWindow) reportWindow.close();
    } finally {
      setGenerating(false);
    }
  }

  return (
    <div className="bg-navy-900 border border-red-500/20 rounded-lg p-3 mt-2 space-y-2.5">
      <p className="text-white/70 text-xs font-medium">Screen {companyName} against your tracked providers</p>
      {providerLists.length === 0 ? (
        <p className="text-white/30 text-xs">
          No My Providers lists yet — you can still screen this vendor for market-wide signals only (no tracked-provider section).
        </p>
      ) : (
        <div className="space-y-1.5 max-h-32 overflow-y-auto">
          {providerLists.map((l) => (
            <label key={l.id} className="flex items-center gap-2 text-xs text-white/60 hover:text-white/80 cursor-pointer">
              <input
                type="checkbox"
                checked={checkedListIds.has(l.id)}
                onChange={() => toggleList(l.id)}
                className="rounded border-white/20 bg-white/5"
              />
              {l.list_name} <span className="text-white/30">— {(l.provider_count || 0).toLocaleString()} providers</span>
            </label>
          ))}
        </div>
      )}
      {genError && <p className="text-red-400 text-xs">{genError}</p>}
      <div className="flex items-center gap-2 pt-1">
        <button
          onClick={handleGenerate}
          disabled={generating}
          className="text-xs px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 disabled:opacity-50 transition-all"
        >
          {generating ? "⏳ Generating…" : checkedListIds.size > 0 ? `🛡 Screen (${checkedListIds.size} list${checkedListIds.size !== 1 ? "s" : ""})` : "🛡 Screen (market-wide only)"}
        </button>
        <button onClick={onCancel} className="text-white/30 hover:text-white/60 text-xs">Cancel</button>
      </div>
    </div>
  );
}